import JSZip from 'jszip';
import { toast } from 'sonner';

export const usePublicGalleryDownload = () => {
  const downloadImagesAsZip = async (images: any[], category: string) => {
    const toastId = toast.loading('Preparing images for download...');
    
    try {
      const zip = new JSZip();
      const folder = zip.folder(`${category}-gallery-images`);
      
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const imageUrl = image.image_url || 
          (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '');
        
        if (!imageUrl) continue;
        
        try {
          let blob;
          if (imageUrl.startsWith('data:image/')) {
            // Base64 to blob
            const base64Data = imageUrl.split(',')[1];
            const mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/png';
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let j = 0; j < binaryString.length; j++) {
              bytes[j] = binaryString.charCodeAt(j);
            }
            blob = new Blob([bytes], { type: mimeType });
          } else {
            const response = await fetch(imageUrl, { mode: 'cors' });
            blob = await response.blob();
          }
          
          const filename = `${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${image.id.slice(0, 8)}.png`;
          folder?.file(filename, blob);
          
          if (i % 10 === 0) {
            toast.loading(`Processing ${i + 1}/${images.length} images...`, { id: toastId });
          }
        } catch (error) {
          console.error(`Failed to process image ${i}:`, error);
        }
      }
      
      toast.loading('Creating ZIP file...', { id: toastId });
      
      // STORE compression = no compression, preserves quality
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'STORE' 
      });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(zipBlob);
      link.download = `gallery-${category}-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success(`Downloaded ${images.length} images successfully!`, { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download images', { id: toastId });
    }
  };

  const downloadSingleImage = async (imageUrl: string, prompt: string, imageId: string) => {
    try {
      let blob;
      if (imageUrl.startsWith('data:image/')) {
        const base64Data = imageUrl.split(',')[1];
        const mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/png';
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: mimeType });
      } else {
        const response = await fetch(imageUrl, { mode: 'cors' });
        blob = await response.blob();
      }
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${imageId.slice(0, 8)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };
  
  return { downloadImagesAsZip, downloadSingleImage };
};
